import { formatDateToISO } from 'support/helpers/dateTime/dateTime';
import {
  ModificationType,
  PricingConfigDTO,
  PricingConfigModificationDTO,
  PricingConfigPayloadLineItemDTO,
  PricingConfigPaymentTermsDTO,
} from 'support/types';
import { FileInputProps } from 'components/Form/FileInput/FileInput';

type ChargeAndDiscount = {
  'Allowance or Charge': ModificationType;
  Amount: string;
  Level: string;
  Calculation: 'ABSOLUTE' | 'RELATIVE';
  'Tax Percent (optional)': string;
  Code: string;
};

type PaymentTerm = {
  Percentage: string;
  'Days Due': string;
};

type LineItem = {
  GTIN: string;
  'Product Name'?: string;
  'Unit Price': string;
  'Sales Tax': string;
  'Item Type': string;
  'Allowance or Charge': ModificationType;
  Calculation: 'ABSOLUTE' | 'RELATIVE';
  Amount: string;
};

export type TemplateData = {
  'Charges and Discounts': Array<ChargeAndDiscount>;
  'Payment Terms': Array<PaymentTerm>;
  Products: Array<LineItem>;
};

type NormalizedData = {
  modifications: Array<PricingConfigModificationDTO>;
  paymentTerms: Array<PricingConfigPaymentTermsDTO>;
  lineItems: Array<PricingConfigPayloadLineItemDTO>;
};

const normalizeChangesAndDiscounts = (data: Array<ChargeAndDiscount>): Array<PricingConfigModificationDTO> => {
  const normalizedData: Array<PricingConfigModificationDTO> = data.map((chargeAndDiscount) => {
    return {
      level: parseInt(chargeAndDiscount.Level),
      type: chargeAndDiscount['Allowance or Charge'],
      code: chargeAndDiscount.Code,
      amount: parseFloat(chargeAndDiscount.Amount),
      isPercentage: chargeAndDiscount.Calculation === 'RELATIVE',
      taxPercent: parseFloat(chargeAndDiscount['Tax Percent (optional)']),
    };
  });

  return normalizedData;
};

const normalizePaymentTerms = (data: Array<PaymentTerm>): Array<PricingConfigPaymentTermsDTO> => {
  const normalizedData: Array<PricingConfigPaymentTermsDTO> = data.map((paymentTerm) => {
    return {
      payInNumberOfDays: parseInt(paymentTerm['Days Due']),
      percentage: parseFloat(paymentTerm.Percentage),
    };
  });

  return [
    {
      percentage: 0,
      payInNumberOfDays: 0,
    },
    ...normalizedData,
  ];
};

const normalizeLineItems = (data: Array<LineItem>): Array<PricingConfigPayloadLineItemDTO> => {
  const normalizedData: Array<PricingConfigPayloadLineItemDTO> = data.map((lineItem) => {
    return {
      gtin: lineItem.GTIN,
      name: lineItem['Product Name'],
      unitPrice: parseFloat(lineItem['Unit Price']),
      salesTax: parseFloat(lineItem['Sales Tax']),
      modifiable: ['RETURNABLE_CONTAINER', 'NON_RETURNABLE_CONTAINER'].includes(lineItem['Item Type']) === false,
      discountOrChargeType: lineItem['Allowance or Charge'],
      discountOrChargeValue: parseFloat(lineItem.Amount),
      discountOrChargeIsPercent: lineItem.Calculation === 'RELATIVE',
    };
  });

  return normalizedData;
};

export const normalizeData = (data: TemplateData): PricingConfigDTO => {
  const normalizedData: NormalizedData = {
    modifications: normalizeChangesAndDiscounts(data['Charges and Discounts']),
    paymentTerms: normalizePaymentTerms(data['Payment Terms']),
    lineItems: normalizeLineItems(data['Products']),
  };

  return {
    id: '',
    name: '',
    payload: normalizedData,
    relationshipId: '',
    partner: {
      id: '',
      name: '',
    },
    createdAt: formatDateToISO(new Date()),
  };
};

export const isValid: Required<FileInputProps>['validate'] = (data: unknown) => {
  if (!data) {
    return { success: false, errorCode: 'empty_file' };
  }
  if (typeof data !== 'object') {
    return { success: false, errorCode: 'invalid_file' };
  }

  const hasChargesAndDiscounts = Object.prototype.hasOwnProperty.call(data, 'Charges and Discounts');
  const hasPaymentTerms = Object.prototype.hasOwnProperty.call(data, 'Payment Terms');
  const hasProducts = Object.prototype.hasOwnProperty.call(data, 'Products');

  if (!hasChargesAndDiscounts) {
    return { success: false, errorCode: 'missing_charges_and_discounts' };
  }
  if (!hasPaymentTerms) {
    return { success: false, errorCode: 'missing_payment_terms' };
  }

  if (!hasProducts) {
    return { success: false, errorCode: 'missing_products' };
  }

  return { success: true };
};
